import * as React from "react";
import { EditorialFlexComponent, EditorialProps } from "../typings";
import {
  UitkCard,
  UitkCardContent,
  UitkCardLink,
  UitkCardMedia,
  UitkCardTextSegment,
  UitkCardImageLayout
} from "uitk-react-cards";
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from "uitk-react-images";
import { UitkScrim, UitkScrimType } from "uitk-react-scrim";
import { inject } from "mobx-react";
import { AspectRatioEnum, ThumborStore } from "stores/thumbor/ThumborStore";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { Action } from "components/utility/analytics/FlexAnalyticsUtils";
import { decorateEditorialHtml } from "../EditorialUtilities";
import { ModuleViewType } from "../Editorial";

const CardMediaImage = inject("thumbor")(
  ({
    thumbor,
    flexComponent,
    context
  }: {
    thumbor?: ThumborStore;
    flexComponent: EditorialFlexComponent;
    context: ExtendedContextStore;
  }) => {
    if (!thumbor) {
      return null;
    }

    return (
      <UitkImage
        alt={
          flexComponent.model.items[0].media ? flexComponent.model.items[0].media[0].mediaAlt : ""
        }
        src={safelyResizeImage(flexComponent, context, thumbor)}
      />
    );
  }
);

export const safelyResizeImage = (
  flexComponent: EditorialFlexComponent,
  context: ExtendedContextStore,
  thumbor: ThumborStore
): string => {
  return thumbor.safelyGenerateThumborizedImageUrl(
    flexComponent.model.items[0].media ? flexComponent.model.items[0].media[0].mediaAlt : "",
    1040,
    580,
    context,
    imageRatio(flexComponent.model.imageAspectRatio),
    true,
    60
  );
};

const imageRatio = (ratio: string) => {
  switch (ratio) {
    case "1-1": {
      return AspectRatioEnum.R_1_1;
    }
    case "3-2": {
      return AspectRatioEnum.R_3_2;
    }
    case "4-3": {
      return AspectRatioEnum.R_4_3;
    }
    case "21-9": {
      return AspectRatioEnum.R_21_9;
    }
    default: {
      return AspectRatioEnum.DEFAULT;
    }
  }
};

const uitkFigureAspectRatio = (ratio: string) => {
  switch (ratio) {
    case "1-1": {
      return UitkFigureAspectRatioType.R1_1;
    }
    case "3-2": {
      return UitkFigureAspectRatioType.R3_2;
    }
    case "4-3": {
      return UitkFigureAspectRatioType.R4_3;
    }
    case "21-9": {
      return UitkFigureAspectRatioType.R21_9;
    }
    default: {
      return UitkFigureAspectRatioType.R16_9;
    }
  }
};

export const EditorialWithLink = (props: EditorialProps) => {
  const moduleName = props.flexComponent.name;
  const cardName =
    props.flexComponent.model.title && props.flexComponent.model.title.replace(" ", "_");
  const rfrr = `${moduleName}.${cardName}.${Action.CLICK}`;
  const cardLabel = props.flexComponent.model.title;
  const cardClassName = "cardEditorialHeading uitk-type-heading-500 uitk-type-left";
  const cardScrimType = UitkScrimType.BOTTOM;
  const cardLink =
    props.flexComponent.model.cardLinkUrl &&
    `${props.flexComponent.model.cardLinkUrl}/?rfrr=${rfrr}`;
  const { flexComponent, context } = props;

  return (
    <UitkCard
      imageLayout={
        flexComponent.model.view === ModuleViewType.Snippet
          ? UitkCardImageLayout.LEFT_FULLBLEED
          : undefined
      }
    >
      <UitkCardMedia pxWidth={flexComponent.model.view === ModuleViewType.Snippet ? 80 : undefined}>
        <UitkFigure ratio={uitkFigureAspectRatio(flexComponent.model.imageAspectRatio)}>
          <CardMediaImage flexComponent={flexComponent} context={context} />
          <UitkScrim type={cardScrimType}>
            <h3 className={cardClassName}>{cardLabel}</h3>
          </UitkScrim>
        </UitkFigure>
        <UitkCardLink>
          <a href={cardLink}>{flexComponent.model.title}</a>
        </UitkCardLink>
      </UitkCardMedia>
      <UitkCardContent>
        <UitkCardTextSegment className="uitk-type-paragraph-200">
          <div
            className="uitk-type-left"
            dangerouslySetInnerHTML={decorateEditorialHtml(flexComponent.model.items[0].text)}
          />
        </UitkCardTextSegment>
      </UitkCardContent>
    </UitkCard>
  );
};

export default EditorialWithLink;
